import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import classnames from 'classnames';

import { addQueryToUrl, removeFromUrl } from 'dpl/util/queryString';
import { preloadImage, isImageLoaded, difference } from 'dpl/shared/utils';
import withLazyLoading from 'dpl/decorators/withLazyLoading';

const IS_BOT_UA = window.__DPL_BOT_UA;

type ImageTag = 'img' | 'div';

interface SmartImageProps {
  Tag?: ImageTag;
  setLazyRef?: ((el: HTMLElement | null) => void) | null;
  className?: string;
  src?: string | null;
  loadingClass?: string;
  minWidth?: string | null;
  minHeight?: string | null;
  width?: string | null;
  height?: string | null;
  alt?: string;
  crop?: boolean;
  forceOriginalSrc?: boolean;
  wrapperOnlyClassName?: string;
  imageOnlyClassName?: string;
  onLoad?: ((target: HTMLImageElement) => void) | null;
  allowContextMenu?: boolean;
  isInViewport: boolean;
}

function getSizeParam(
  element: HTMLElement | null,
  width: string | null,
  height: string | null
) {
  const devicePixelRatio = window.devicePixelRatio || 1;

  let finalWidth = parseInt(width as string, 10);
  let finalHeight = parseInt(height as string, 10);

  if (element && !(finalWidth && finalHeight)) {
    const rect = element.getBoundingClientRect();
    finalWidth = rect.width;
    finalHeight = rect.height;
  }

  finalWidth = Math.ceil(finalWidth * devicePixelRatio);
  finalHeight = Math.ceil(finalHeight * devicePixelRatio);

  return `${finalWidth}x${finalHeight || finalWidth}`;
}

function getScrappyUrl(
  url: string,
  width: string | null,
  height: string | null,
  crop: boolean,
  element: HTMLElement | null
) {
  const isManuallyCropped = url.includes('manual');

  if (!isManuallyCropped) {
    url = removeFromUrl(['resize', 'crop'], url);
  }

  return addQueryToUrl(
    { [crop ? 'crop' : 'resize']: getSizeParam(element, width, height) },
    url
  );
}

const SmartImage = React.memo<SmartImageProps>(
  ({
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Tag: TagProp = 'img',
    setLazyRef = null,
    className = '',
    src = null,
    loadingClass = '',
    minWidth = null,
    minHeight = null,
    width = null,
    height = null,
    alt = '',
    crop = false,
    forceOriginalSrc = false,
    wrapperOnlyClassName = '',
    imageOnlyClassName = '',
    onLoad = null,
    allowContextMenu = false,
    isInViewport
  }) => {
    let Tag: ImageTag = TagProp;
    const ref = useRef<HTMLElement | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const willUnmountRef = useRef(false);

    if (IS_BOT_UA) {
      Tag = 'img';
    }

    useEffect(() => {
      if (!src) {
        return;
      }
      if (!IS_BOT_UA && !isInViewport) return;

      let newSrc = src;

      if (!forceOriginalSrc && !/^(blob|data):/.test(src)) {
        newSrc = getScrappyUrl(src, width, height, crop, ref.current);
      }

      if (newSrc === imageSrc) return;

      setImageSrc(newSrc);
      setIsLoading(!isImageLoaded(newSrc));

      preloadImage(newSrc)
        // eslint-disable-next-line no-console
        .catch(() => console.error(`Failed to preload image ${newSrc}`))
        .then((target: any) => {
          if (!willUnmountRef.current && target) {
            setIsLoading(false);
            onLoad?.(target);
          }
        });
    }, [src, isInViewport, forceOriginalSrc, crop, width, height, ref.current]);

    useEffect(
      () => () => {
        willUnmountRef.current = true;
      },
      []
    );

    const dimensionStyles = useMemo(
      () => ({
        width: width ?? undefined,
        height: height ?? undefined,
        minWidth: minWidth ?? undefined,
        minHeight: minHeight ?? undefined
      }),
      [width, height, minWidth, minHeight]
    );

    const elementProps = useMemo(() => {
      if (Tag === 'img') {
        return {
          alt,
          src: imageSrc ?? undefined,
          style: {
            ...dimensionStyles,
            userDrag: 'none',
            WebkitUserDrag: 'none',
            userSelect: 'none' as const
          }
        };
      }

      return {
        role: 'img',
        title: alt,
        'aria-label': alt,
        style: {
          ...dimensionStyles,
          backgroundImage: imageSrc ? `url('${imageSrc}')` : undefined
        }
      };
    }, [Tag, alt, imageSrc, dimensionStyles]);

    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => {
        if (!allowContextMenu) {
          e.preventDefault();
        }
      },
      [allowContextMenu]
    );

    const setRefCallback = useCallback(
      (el: HTMLElement | null) => {
        ref.current = el;
        setLazyRef?.(el);
      },
      [setLazyRef]
    );

    const outerClassName = useMemo(() => {
      if (!imageOnlyClassName) return className;
      return difference(
        className.split(' '),
        imageOnlyClassName.split(' ')
      ).join(' ');
    }, [className, imageOnlyClassName]);

    return (
      <div
        data-test-id="smart-image-wrapper"
        className={classnames(
          'SmartImage f0 dib',
          outerClassName,
          wrapperOnlyClassName,
          { [loadingClass]: isLoading }
        )}
        style={dimensionStyles as React.CSSProperties}
        ref={setRefCallback}
      >
        {imageSrc && (
          <Tag
            {...elementProps}
            className={classnames('SmartImage__image', className, {
              'o-0': !IS_BOT_UA && isLoading
            })}
            onContextMenu={handleContextMenu}
          />
        )}
      </div>
    );
  }
);

SmartImage.displayName = 'SmartImage';

export default withLazyLoading()(SmartImage);
